import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import {
	AuthSelector,
	CustomerComplianceService,
	LimitsModalType,
	LimitsReminderAction,
	LimitsReminderConfigService,
	LimitsReminderSelector,
	LogoutPayload,
	SESSION_ACTION_TYPE,
} from "@obg/ngx.core";
import { CoreContentState } from "@obg/ngx.core.content";
import { filter, map, tap, withLatestFrom } from "rxjs/operators";

import { LimitsReminderService } from "./limits-reminder.service";

@Injectable()
export class LimitsReminderEffect {
	initializeLimitsReminderType$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(LimitsReminderAction.initializeLimitsReminderType),
				tap(() => this.limitsReminderService.updateGameIdFromCache())
			),
		{ dispatch: false }
	);

	updateLastGameId$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(LimitsReminderAction.updateLastGameId),
				tap(({ gameId }) => {
					this.limitsReminderService.saveLastGameIdIntoCache(gameId);
				})
			),
		{ dispatch: false }
	);

	showLimitsReminder$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(LimitsReminderAction.showLimitsReminder),
				tap(() => this.limitsReminderService.showLimitsReminder())
			),
		{ dispatch: false }
	);

	showLimitsReminderOnLogoutSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.logoutSuccess),
			map((action: ActionWithPayload<LogoutPayload>) => action),
			withLatestFrom(this.customerComplianceService.isDutchKycRequested$()),
			filter(
				([{ payload }, isDutchKycRequested]) =>
					this.config.isLimitsReminderEnabled() && !isDutchKycRequested && !payload?.shouldSkipNotification
			),
			map(() => LimitsReminderAction.showLimitsReminder({ payload: LimitsModalType.logout }))
		)
	);

	showLimitsReminderOnGameLaunch$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LimitsReminderAction.gameLaunch),
			withLatestFrom(
				this.store.select(LimitsReminderSelector.getLastGameId),
				this.store.select(AuthSelector.isAuthenticated),
				this.customerComplianceService.isDutchKycRequested$()
			),
			filter(
				([{ gameId }, lastGameId, isAuthenticated, isDutchKycRequested]) =>
					isAuthenticated && !isDutchKycRequested && gameId !== lastGameId
			),
			map(([{ gameId }]) =>
				batchActions(
					LimitsReminderAction.updateLastGameId(gameId),
					LimitsReminderAction.showLimitsReminder({ payload: LimitsModalType.gameSwitch })
				)
			)
		)
	);

	constructor(
		private actions$: Actions,
		private store: Store<CoreContentState>,
		private limitsReminderService: LimitsReminderService,
		private config: LimitsReminderConfigService,
		private customerComplianceService: CustomerComplianceService
	) {}
}
